/**
 * THE ROOM — what the meeting screen states about itself, worked out away from the JSX.
 *
 * From `design_handoff_rules_ia` §3 ("In session"). Three things there are arithmetic dressed up
 * as graphics, and all three are wrong in ways nobody notices at nine in the evening if they are
 * computed inline:
 *
 *   THE STATUS LINE counts a quorum that includes proxies (2015-I-1). `representedSplit` already
 *   does that; this puts the two halves into the sentence the room reads, because "9 of 10" with
 *   seven people sitting there is a claim somebody will challenge out loud.
 *
 *   THE THRESHOLD TRACK draws the thing that decides pass or fail. The handoff's reasoning is
 *   that the numbers 6 and 7 interact with attendance and with whether the item is a re-vote, and
 *   a sentence stating all of that is a sentence nobody finishes reading. Ten cells and two ticks
 *   say it at a glance — but only if the cells are filled by the same rule `tallyOutcome` decides
 *   by, which is why the state line here is derived from the yes count and the represented count
 *   rather than written by hand beside them.
 *
 *   A DECIDED ITEM carries a word, not a colour. Red on near-black is the weakest contrast pair
 *   available and a colour-blind reader gets nothing from it, so `decidedRow` returns the word and
 *   the rail tone together and the rail is never the only carrier.
 *
 * GREEN IS THE LIVE ITEM'S ALONE in the room, which is why a passed item's rail is `#3f4a5c` and
 * not a green. A decided item competing with the one that is happening is the failure this avoids.
 */

import {
  LEAGUE_SIZE, QUORUM, RELAXED_MAJORITY, SUPER_MAJORITY,
  type ProposalOutcome, type Vote, type VoteTally,
} from './proposals.js';
import { isLodged, representedSplit, type ProxyBallot } from './proxy.js';
import type { GmId } from '../store/types.js';

/* -- the status bar ---------------------------------------------------------------------------- */

export interface RoomStatus {
  inRoom: number;
  byProxy: number;
  represented: number;
  quorum: boolean;
  closed: number;
  live: number;
  toGo: number;
  /** 1-based position of the open item, or null when none is open. */
  itemNo: number | null;
  total: number;
  /** `Quorum 9 of 10 — 7 present, 2 by proxy` */
  line: string;
  /**
   * `item 2 of 6`, kept apart from the quorum sentence so it can be held on one line.
   *
   * Joined into one string it wrapped as "item 2 / of 6" on a phone, which is the kind of break
   * that makes a reader re-read a number they had already read correctly.
   */
  where: string;
  /** `1 closed · 1 live · 4 to go` */
  shape: string;
}

export function roomStatus(input: {
  gms: readonly GmId[];
  absent: readonly GmId[];
  proxies: readonly ProxyBallot[];
  order: readonly string[];
  openItemId?: string;
  closedIds?: readonly string[];
}): RoomStatus {
  const split = representedSplit(input.gms, input.absent, input.proxies);
  const total = input.order.length;
  const closedSet = new Set(input.closedIds ?? []);
  const closed = input.order.filter((id) => closedSet.has(id)).length;
  const idx = input.openItemId ? input.order.indexOf(input.openItemId) : -1;
  const live = idx >= 0 ? 1 : 0;
  const toGo = Math.max(0, total - closed - live);

  const parts = [`${split.inRoom} present`];
  if (split.byProxy > 0) parts.push(`${split.byProxy} by proxy`);

  const line = `Quorum ${split.total} of ${input.gms.length} — ${parts.join(', ')}`;
  const where = idx >= 0
    ? `item ${idx + 1} of ${total}`
    : total ? `${total} on the docket` : '';

  /*
   * THE NIGHT'S SHAPE omits an empty bucket rather than printing `0 closed`. Nought of something
   * is not a state of the evening; it is a state of the sentence.
   */
  const shape = [
    closed ? `${closed} closed` : '',
    live ? '1 live' : '',
    toGo ? `${toGo} to go` : '',
  ].filter(Boolean).join(' · ');

  return {
    inRoom: split.inRoom,
    byProxy: split.byProxy,
    represented: split.total,
    quorum: split.total >= QUORUM,
    closed,
    live,
    toGo,
    itemNo: idx >= 0 ? idx + 1 : null,
    total,
    line,
    where,
    shape,
  };
}

/* -- the threshold track ------------------------------------------------------------------------ */

/** One of the ten seats: filled from the room, filled by a counted proxy, or still empty. */
export type Cell = 'room' | 'proxy' | 'empty';

export interface Tick {
  /** How many yes votes this mark sits at. */
  at: number;
  label: string;
  /** True once the count has reached it. */
  reached: boolean;
}

export interface Track {
  cells: Cell[];
  ticks: Tick[];
  /** The sentence under the track. Never a bare count. */
  state: string;
  standing: ProposalOutcome | 'short';
}

/**
 * WHICH YES VOTES CAME BY PROXY, without storing it.
 *
 * A commissioner casting a lodged instruction writes an ordinary vote under the absent GM's name
 * — which is correct, it IS his vote — so the record cannot distinguish it afterwards and should
 * not try. What identifies it is the pair of facts that were true when it was cast: the man is on
 * the absence register and holds a live proxy. Deriving it here means no second source of truth
 * to fall out of step with the first.
 */
export function yesSplit(
  votes: Readonly<Record<string, Vote>>,
  absent: readonly GmId[],
  proxies: readonly ProxyBallot[],
): { room: number; proxy: number } {
  const away = new Set(absent.map((g) => g.toUpperCase()));
  const held = new Set(proxies.filter(isLodged).map((b) => b.gm.toUpperCase()));
  let room = 0;
  let proxy = 0;
  for (const [gm, v] of Object.entries(votes)) {
    if (v !== 'yes') continue;
    if (away.has(gm.toUpperCase()) && held.has(gm.toUpperCase())) proxy += 1;
    else room += 1;
  }
  return { room, proxy };
}

export interface TrackInput {
  yesInRoom: number;
  yesByProxy: number;
  represented: number;
  isRevote?: boolean;
  proposerObjects?: boolean;
  seats?: number;
}

export function thresholdTrack(input: TrackInput): Track {
  const seats = input.seats ?? LEAGUE_SIZE;
  const yes = input.yesInRoom + input.yesByProxy;
  const cells: Cell[] = Array.from({ length: seats }, (_, i) => (
    i < input.yesInRoom ? 'room' : i < yes ? 'proxy' : 'empty'
  ));

  /*
   * A RE-VOTE HAS ONE THRESHOLD, not two (2019-I-2). Drawing the relaxed mark on an item where it
   * does not apply is worse than drawing nothing: it is a line the room can see itself reaching
   * while the rule lapses anyway.
   */
  const ticks: Tick[] = input.isRevote
    ? [{ at: SUPER_MAJORITY, label: 'REQUIRED', reached: yes >= SUPER_MAJORITY }]
    : [
      { at: RELAXED_MAJORITY, label: 'RENEWS', reached: yes >= RELAXED_MAJORITY },
      { at: SUPER_MAJORITY, label: 'PERMANENT', reached: yes >= SUPER_MAJORITY },
    ];

  return { cells, ticks, ...standingOf(yes, input, seats) };
}

function standingOf(
  yes: number,
  input: TrackInput,
  seats: number,
): { state: string; standing: ProposalOutcome | 'short' } {
  if (input.represented < QUORUM) {
    return {
      state: `Only ${input.represented} represented. A quorum of ${QUORUM} comes first.`,
      standing: 'no-quorum',
    };
  }
  if (input.proposerObjects) {
    return {
      state: 'A proposer is against the modification. It cannot pass at any count.',
      standing: 'failed',
    };
  }
  if (yes >= SUPER_MAJORITY) {
    return { state: `Permanent at ${yes}.`, standing: 'passed' };
  }
  if (input.isRevote) {
    const short = SUPER_MAJORITY - yes;
    return {
      state: `${short} short of the ${SUPER_MAJORITY} a re-vote needs. It lapses below that.`,
      standing: 'short',
    };
  }
  if (yes >= RELAXED_MAJORITY && input.represented < seats) {
    const short = SUPER_MAJORITY - yes;
    return {
      state: `Renewal reached at ${yes}. ${countWord(short)} short of permanent.`,
      standing: 'provisional',
    };
  }
  if (yes >= RELAXED_MAJORITY) {
    return {
      state: `${yes} yes with all ${seats} represented — the relaxed ${RELAXED_MAJORITY} does not `
        + `apply, so this needs ${SUPER_MAJORITY}.`,
      standing: 'short',
    };
  }
  const toRenew = RELAXED_MAJORITY - yes;
  return {
    state: input.represented < seats
      ? `${countWord(toRenew)} short of a renewal, ${SUPER_MAJORITY - yes} short of permanent.`
      : `${SUPER_MAJORITY - yes} short of the ${SUPER_MAJORITY} needed with everyone represented.`,
    standing: 'short',
  };
}

/** Small numbers read as words in a sentence and as digits in a tally. This is a sentence. */
function countWord(n: number): string {
  return ['None', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven'][n] ?? String(n);
}

/* -- a decided item ------------------------------------------------------------------------------ */

export interface DecidedRow {
  tone: 'passed' | 'failed';
  /** The outcome as a word, because the rail colour is never the only carrier. */
  word: string;
  /** `7–3`, yes against everything that counted against it. */
  margin: string;
  /** `ONE SEASON` or `LAPSES`, the consequence a bare outcome hides. */
  flag: string | null;
}

export function decidedRow(
  p: { isRevote?: boolean; status?: string },
  tally: VoteTally,
  outcome: ProposalOutcome,
): DecidedRow {
  const margin = `${tally.yes}–${tally.no + tally.abstain}`;
  if (outcome === 'passed') return { tone: 'passed', word: 'Passed', margin, flag: null };
  if (outcome === 'provisional') {
    return { tone: 'passed', word: 'Passed', margin, flag: 'ONE SEASON' };
  }
  if (outcome === 'no-quorum') return { tone: 'failed', word: 'No quorum', margin, flag: null };
  /*
   * A FAILED RE-VOTE IS NOT THE SAME EVENT AS A FAILED PROPOSAL. One leaves the rulebook where it
   * was; the other takes a rule that is in force today out of it tonight. The rail cannot tell
   * them apart, so the flag does.
   */
  return { tone: 'failed', word: 'Failed', margin, flag: p.isRevote ? 'LAPSES' : null };
}
